/**
 * Integrytas Group — Realtime subscription guard.
 *
 * Every live-update feature (task queue, notifications, CMM soft locks) opens a
 * Supabase Realtime channel. Realtime is an OPTIONAL part of the stack: the
 * supabase CLI config ships `[realtime] enabled = false`, and a self-hosted
 * deployment may not run the container either. When it is absent the client
 * still tries to open the socket, fails, reconnects, fails again — filling the
 * console with WebSocket errors for a capability nobody asked for.
 *
 * That is a fail-open problem, and §1.5 already says how the platform handles
 * one: if a module is absent, traffic still flows. The data these hooks show is
 * never at risk — each fetches through `apiDb` first and uses Realtime only to
 * stay current — so the correct degradation is "no live updates", not "retry
 * forever".
 *
 * So: the first channel that errors latches Realtime off and drops EVERY
 * channel. Removing the last one makes supabase-js disconnect the socket, which
 * is what actually stops the retry loop — suppressing the log alone would leave
 * it reconnecting unseen.
 *
 * Dropping all of them, rather than just the one that reported, is what gets
 * this down to a single failure. The hooks all mount together and share one
 * socket, so they have each registered a channel before any error comes back;
 * removing only the reporting channel leaves the others to pull the socket up
 * again for a second round of failures.
 *
 * The latch EXPIRES rather than lasting the session. A permanently absent
 * server then costs one attempt per cooldown instead of a continuous storm,
 * while a transient production blip heals on its own instead of silently
 * disabling live updates until the user reloads.
 */

import type { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from './supabase_client';

/** How long to stop attempting after a failure. */
const RETRY_AFTER_MS = 5 * 60_000;

/**
 * Opt out entirely with `VITE_REALTIME_ENABLED=false` — for a deployment that
 * knows it has no Realtime and wants not even the periodic retry. Anything else
 * (including unset) leaves the auto-detection above in charge.
 */
const FORCED_OFF = import.meta.env.VITE_REALTIME_ENABLED === 'false';

/** Epoch ms before which no new subscription is attempted. */
let unavailableUntil = 0;
let warned = false;

/** Statuses supabase-js reports when the channel could not be established. */
const FAILED_STATUSES = ['CHANNEL_ERROR', 'TIMED_OUT'];

/** Cleanup for the case where no channel was opened. */
function noop(): void { /* nothing was subscribed */ }

export function isRealtimeAvailable(): boolean {
  return !FORCED_OFF && Date.now() >= unavailableUntil;
}

/**
 * Subscribe through the guard.
 *
 * @param build  Creates and configures the channel — called only when Realtime
 *               is believed available, so a caller never builds one needlessly.
 * @returns      An unsubscribe function safe to use as a useEffect cleanup,
 *               whether or not a channel was actually opened.
 */
export function subscribeRealtime(build: () => RealtimeChannel): () => void {
  if (!isRealtimeAvailable()) return noop;

  const channel = build();

  channel.subscribe((status: string) => {
    if (!FAILED_STATUSES.includes(status)) return;

    unavailableUntil = Date.now() + RETRY_AFTER_MS;
    // Dropping the channels is the part that matters: with none left,
    // supabase-js closes the socket and stops reconnecting. All of them, not
    // just this one — see the note above.
    void supabase.removeAllChannels();

    if (!warned) {
      warned = true;
      console.warn(
        '[CSM] Realtime unavailable — live updates are off; screens still load ' +
        'and refresh normally. Set `[realtime] enabled = true` in ' +
        'supabase/config.toml (or run the realtime container) to enable them.',
      );
    }
  });

  return () => { void supabase.removeChannel(channel); };
}

/** Test seam — restores the module to its initial state. */
export function __resetRealtimeGuardForTests(): void {
  unavailableUntil = 0;
  warned = false;
}
